<template>
  <BaseModal
    :is-open="isOpen"
    title="Do you want to report this listing?"
    @close="isOpen = false"
  >
    <div>
      If so, please choose a reason:
    </div>

    <div v-auto-animate>
      <FormLabel
        v-for="(option) in options"
        :key="option.title"
        class="cursor-pointer flex justify-start font-normal my-6"
      >
        <div class="pt-0.5">
          <RadioField
            v-model="reason"
            name="reason"
            :val="option.title"
            @change="isWarningVisible = false"
          >
            <template />
          </RadioField>
        </div>
        <div>
          <div class="font-semibold">
            {{ option.title }}
          </div>
          <div class="mt-1 text-xs">
            {{ option.desc }}
          </div>
        </div>
      </FormLabel>

      <FormCaption v-if="isWarningVisible" class="mt-4" type="invalid">
        Please provide a reason for reporting this listing
      </FormCaption>
    </div>

    <template #actions>
      <BaseButton
        class="w-full im:w-auto"
        color="red"
        :is-loading="isLoading"
        @click="save"
      >
        Report
      </BaseButton>

      <BaseButton
        class="w-full mt-2 im:mt-0 im:w-auto im:mr-2"
        color="white"
        @click="isOpen = false"
      >
        Cancel
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script setup>
import { onMounted, ref } from 'vue';

import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import FormCaption from '@/components/Ui/Forms/FormCaption.vue';
import FormLabel from '@/components/Ui/Forms/FormLabel.vue';
import RadioField from '@/components/Ui/Forms/RadioField.vue';
import BaseModal from '@/components/Ui/Modals/BaseModal.vue';
import { fillVariables } from '@/services/templates';
import { useToaster } from '@/services/toaster';
import request from '@/utils/request';

const props = defineProps({
  venueId: {
    type: Number,
    required: true,
  },
  venueName: {
    type: String,
    required: true,
  },
});
const { showNotification } = useToaster();

const isOpen = ref(false);
const reason = ref(null);
const isWarningVisible = ref(false);
const isLoading = ref(false);

const options = [
  {
    title: 'This listing shouldn’t be on Tagvenue',
    desc: 'This listing contains false information or may be a fake listing',
  },
  {
    title: 'Inappropriate content or spam',
    desc: 'The description of this listing contains graphic, promotional or otherwise offensive content',
  },
  {
    title: 'Inappropriate or deceptive photos ',
    desc: 'The photos of this listing don’t match its description',
  },
];

const save = async () => {
  if (!reason.value) {
    isWarningVisible.value = true;
    return;
  }

  let user = 'Guest';
  const userData = window.__TGV_USER_DATA;
  if (userData?.type === 'manager') {
    user = `Manager ID: ${userData.id}`;
  } else if (userData?.type === 'user') {
    user = `User ID: ${userData.id}`;
  }

  const data = {
    user,
    venueId: props.venueId,
    venueName: props.venueName,
    reason: reason.value,
  };

  const url = fillVariables('/venues/report/{venueId}', {
    venueId: props.venueId,
  });

  await request(url, {
    method: 'POST',
    data,
  });

  isLoading.value = false;
  showNotification.success('Our team will review this profile to ensure details are accurate for our community.', {
    title: 'Thank you for your report.',
  });
  isOpen.value = false;
};

onMounted(() => {
  window.triggerReportModal = () => {
    reason.value = null;
    isWarningVisible.value = false;
    isLoading.value = false;

    isOpen.value = true;
  };
});
</script>
